import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { select, pointer } from 'd3-selection';
import { useD3DblClickCallback } from '@view-model/models/common/hooks';
import { contentTransformAtom } from '@user/pages/ViewModelPage/contentTransformAtom';
import { useAtom } from 'jotai/react';
import type { D3DragEvent } from 'd3';
import { HandleD3Drag } from '@view-model/models/common/d3/HandleD3Drag';
import { D3Mouse } from '@view-model/models/common/d3/D3Mouse';
import { useUserOperationModes } from '@user/pages/ViewModelPage/UserOperationModesProvider';
import { Rect } from '@view-model/models/common/basic/Rect';
import { RectSelectorView } from '@view-model/ui/components/Model/ModelCanvas/RectSelectorView';
import { classNames } from '@framework/utils';
import type { PositionPair } from '@view-model/models/common/types/ui';

type Props = {
  children: React.ReactNode;
  onMousePointerDetected: (x: number, y: number) => void;
  onRectSelection: () => void;
  onRectSelectionEnd: (rect: Rect) => void;
};

export const MousePointerDetector: React.FC<Props> = (props: Props) => {
  const { children, onMousePointerDetected, onRectSelection, onRectSelectionEnd } = props;
  const [contentTransform] = useAtom(contentTransformAtom);
  const pointerDetectorRef = useRef<SVGGElement>(null);
  const pointsRef = useRef<PositionPair>({ start: null, end: null });
  const d3Mouse = useMemo(() => new D3Mouse(pointerDetectorRef), []);
  const { userOperationMode } = useUserOperationModes();
  const [draggingRect, setDraggingRect] = useState<Rect | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const checkDraggingMultiSelectionDisabled = useCallback(
    (e: MouseEvent): boolean => {
      if (userOperationMode === 'map') return !(e.shiftKey || e.ctrlKey || e.metaKey);

      return false;
    },
    [userOperationMode]
  );

  useEffect(() => {
    const target = pointerDetectorRef.current;
    select(target).on('mousemove', (event) => {
      const [x, y] = pointer(event);
      onMousePointerDetected(x, y);
    });

    return () => {
      select(target).on('mousemove', null);
    };
  }, [pointerDetectorRef, onMousePointerDetected]);

  // ドラッグによるビュー複数選択のためのuseEffect
  useEffect(() => {
    const onDragStart = function (event: D3DragEvent<Element, unknown, unknown>) {
      const [x, y] = d3Mouse.getPosition(event);
      pointsRef.current.start = { x, y };
    };

    const onDrag = function (event: D3DragEvent<Element, unknown, unknown>) {
      const [x, y] = d3Mouse.getPosition(event);
      pointsRef.current.end = { x, y };
      setIsDragging(true);

      onRectSelection();

      const { start, end } = pointsRef.current;
      if (start && end) {
        setDraggingRect(Rect.fromPositions([start, end]));
      }
    };

    const onDragEnd = function () {
      const { start, end } = pointsRef.current;

      if (start && end) {
        onRectSelectionEnd(Rect.fromPositions([start, end]));
      }
      pointsRef.current = { start: null, end: null };

      setIsDragging(false);
      setDraggingRect(null);
    };

    // 複数ビュー選択モード時はドラッグによる要素の複数選択を抑止
    const handleD3Drag = new HandleD3Drag(pointerDetectorRef, checkDraggingMultiSelectionDisabled);
    handleD3Drag.onMounted(onDrag, onDragStart, onDragEnd);
    return () => {
      handleD3Drag.onWillUnmounted();
    };
  }, [onRectSelection, onRectSelectionEnd, userOperationMode, d3Mouse, checkDraggingMultiSelectionDisabled]);

  useD3DblClickCallback(pointerDetectorRef, () => void 0, true);

  return (
    <g
      ref={pointerDetectorRef}
      transform={`translate(${contentTransform.x}, ${contentTransform.y}) scale(${contentTransform.k})`}
      // ドラッグ中はマウスカーソルをデフォルトに固定
      className={classNames(isDragging ? '[&_*]:!cursor-default' : '')}
    >
      {children}
      {draggingRect && <RectSelectorView rect={draggingRect} />}
    </g>
  );
};
